import { CLINIC } from './clinic';

// NOTE: swap SITE_URL to the real live domain before launch (used for canonical, hreflang, OG, sitemap).
export const SITE_URL = 'https://discspinedecompression.com';
export const OG_IMAGE = 'https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=1200';

// Google rating (shown visibly on-site + used in AggregateRating schema)
export const RATING = { value: '5.0', count: 63 };

export const SERVICE_AREAS = [
  'Westlake Austin', 'West Lake Hills', 'Rollingwood', 'Tarrytown',
  'Bee Cave', 'Lakeway', 'South Austin', 'Downtown Austin',
];

// Sitewide LocalBusiness / Chiropractor structured data
export const LOCAL_BUSINESS_LD = {
  '@context': 'https://schema.org',
  '@type': 'Chiropractic',
  '@id': `${SITE_URL}/#clinic`,
  name: 'Disc, Spine & Decompression',
  image: OG_IMAGE,
  url: SITE_URL,
  telephone: CLINIC.phone,
  email: CLINIC.email,
  priceRange: '$$',
  medicalSpecialty: 'Chiropractic',
  currenciesAccepted: 'USD',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '1011 Westlake Dr',
    addressLocality: 'Austin',
    addressRegion: 'TX',
    postalCode: '78746',
    addressCountry: 'US',
  },
  geo: { '@type': 'GeoCoordinates', latitude: 30.2985, longitude: -97.8060 },
  areaServed: SERVICE_AREAS.map((a) => ({ '@type': 'City', name: a })),
  availableLanguage: ['English', 'Spanish'],
  aggregateRating: { '@type': 'AggregateRating', ratingValue: '5.0', reviewCount: '63', bestRating: '5', worstRating: '1' },
  employee: {
    '@type': 'Physician',
    name: 'Dr. Ruiz',
    jobTitle: 'Chiropractor',
    medicalSpecialty: 'Chiropractic',
    description: 'Chiropractor with 30 years of clinical experience specializing in non-surgical spinal decompression, sports and auto-injury care.',
  },
  knowsAbout: ['Chiropractic', 'Spinal Decompression', 'DRX9000', 'Sciatica', 'Herniated Disc', 'Whiplash', 'Auto Accident Injury', 'Sports Injury', 'Shockwave Therapy'],
  availableService: [
    { '@type': 'MedicalTherapy', name: 'Non-Surgical Spinal Decompression (DRX9000)' },
    { '@type': 'MedicalTherapy', name: 'Chiropractic Adjustment' },
    { '@type': 'MedicalTherapy', name: 'PiezoWave2 Shockwave Therapy' },
    { '@type': 'MedicalTherapy', name: 'Auto Accident & Whiplash Injury Care' },
    { '@type': 'MedicalTherapy', name: 'Sports Injury Recovery' },
    { '@type': 'MedicalTherapy', name: 'Corrective Chiropractic Care (CBP)' },
  ],
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], opens: '07:00', closes: '12:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], opens: '14:00', closes: '19:00' },
  ],
};

// FAQ + MedicalWebPage builder for condition landing pages (operates on a resolved, language-specific object)
export const buildConditionLd = (c, url) => [
  {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: c.h1,
    description: c.description,
    url,
    about: { '@type': 'MedicalCondition', name: c.condition },
    provider: { '@id': `${SITE_URL}/#clinic` },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: c.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
];

// 8 condition landing pages (Phase 1) — English top-level, Spanish under `es`.
export const CONDITIONS_SEO = [
  {
    slug: 'sciatica', condition: 'Sciatica', modality: 'Chiropractic & Decompression Evaluation',
    title: 'Sciatica Treatment Austin TX | Chiropractic & Decompression',
    description: 'Austin evaluation and conservative care for sciatica. Disc, Spine & Decompression in Westlake Austin.',
    h1: 'Sciatica Treatment in Austin',
    intro: 'Sciatica describes pain or neurologic symptoms that travel along the distribution of the sciatic nerve, often into the buttock or leg. The important question is what is producing the symptoms. Disc irritation, narrowing around spinal structures and other musculoskeletal problems can produce similar patterns.',
    body: 'We evaluate the history, distribution of symptoms, aggravating factors, neurologic findings and available imaging. Depending on the findings, a conservative plan may include chiropractic care, DRX9000 decompression or other appropriate measures. When symptoms correlate with a disc presentation and the patient is an appropriate candidate, decompression may be considered as one non-surgical option. Progressive weakness, bowel or bladder changes or other red flags require appropriate medical attention.',
    faqs: [
      { q: 'Does every case of sciatica have the same cause?', a: 'No. Sciatica-type symptoms can arise from several mechanisms, so evaluation matters before choosing chiropractic care, decompression, another approach or referral.' },
      { q: 'Can spinal decompression help sciatica?', a: 'Some sciatica presentations relate to disc or spinal factors for which decompression is considered in appropriately selected patients. It is not right for everyone.' },
    ],
  },
  {
    slug: 'herniated-disc', condition: 'Herniated Disc', modality: 'Non-Surgical Options',
    title: 'Herniated Disc Treatment Austin | Non-Surgical Options',
    description: 'Austin evaluation and conservative care for herniated disc concerns. Disc, Spine & Decompression in Westlake Austin.',
    h1: 'Herniated Disc Care in Austin',
    intro: 'A herniated disc can occur in the cervical or lumbar spine and may or may not be symptomatic. When symptoms are present, they can include localized pain, radiating pain, numbness, tingling or weakness depending on the structures involved.',
    body: 'MRI language should be correlated with the patient\u2019s examination; we review available imaging in context rather than treating the image alone. Appropriately selected patients may be evaluated for DRX9000 decompression, chiropractic care or a combined conservative strategy. Significant neurologic deficits, red flags or failure to respond appropriately can require additional medical evaluation.',
    faqs: [
      { q: 'Does a herniated disc always cause pain?', a: 'No. A herniated disc may or may not be symptomatic. Findings should be correlated with the clinical examination.' },
      { q: 'What non-surgical options are available?', a: 'Selected patients may be evaluated for DRX9000 decompression, chiropractic care or a combined conservative plan, with referral when clinically indicated.' },
    ],
  },
  {
    slug: 'bulging-disc', condition: 'Bulging Disc', modality: 'Disc Decompression',
    title: 'Bulging Disc Treatment Austin | Disc Decompression',
    description: 'Austin evaluation and conservative care for bulging disc concerns. Disc, Spine & Decompression in Westlake Austin.',
    h1: 'Bulging Disc Care in Austin',
    intro: 'Disc bulges are common imaging findings, particularly with age, and not every bulge causes pain. Treatment decisions should be based on the patient\u2019s symptoms and examination rather than the MRI phrase by itself.',
    body: 'A bulging disc may be relevant when the location and clinical findings correlate, and other pain generators can coexist. Selected cases may be considered for decompression or chiropractic management after appropriate screening. We reassess function and symptoms so care can be adjusted rather than continued automatically.',
    faqs: [
      { q: 'Is a bulging disc the same as a herniation?', a: 'They are different imaging descriptions. Both must be interpreted alongside the clinical presentation rather than treated in isolation.' },
      { q: 'How do you decide on treatment?', a: 'After appropriate screening, selected cases may be considered for decompression or chiropractic care, with progress reassessed over time.' },
    ],
  },
  {
    slug: 'spinal-stenosis', condition: 'Spinal Stenosis', modality: 'Conservative Spine Care',
    title: 'Spinal Stenosis Austin | Conservative Spine Care',
    description: 'Austin evaluation and conservative care for spinal stenosis. Disc, Spine & Decompression in Westlake Austin.',
    h1: 'Conservative Care for Spinal Stenosis in Austin',
    intro: 'Spinal stenosis refers to narrowing within areas of the spine and can have different causes and degrees of severity. Symptoms can include back or neck pain, leg symptoms, walking intolerance or neurologic complaints.',
    body: 'Age, anatomy, symptom severity and neurologic status matter. Some patients are reasonable candidates for conservative management while others need specialist evaluation. Some patients with stenosis ask about non-surgical decompression; we evaluate candidacy carefully and avoid presenting decompression as a universal solution. Progressive neurologic symptoms or concerning findings warrant timely medical evaluation.',
    faqs: [
      { q: 'Is decompression a guaranteed solution for stenosis?', a: 'No. We evaluate candidacy carefully and do not present decompression as a universal solution. Some patients need specialist evaluation.' },
      { q: 'When should stenosis be seen urgently?', a: 'Progressive neurologic symptoms or other concerning findings warrant timely medical evaluation.' },
    ],
  },
  {
    slug: 'degenerative-disc-disease', condition: 'Degenerative Disc Disease', modality: 'Spine & Decompression Care',
    title: 'Degenerative Disc Disease Austin | Spine & Decompression Care',
    description: 'Austin evaluation and conservative care for degenerative disc disease. Disc, Spine & Decompression in Westlake Austin.',
    h1: 'Degenerative Disc Disease Care in Austin',
    intro: 'Degenerative disc changes are common and can range from incidental imaging findings to clinically relevant contributors to pain and reduced function. Our focus is determining how the patient\u2019s symptoms and examination relate to those changes.',
    body: 'Depending on the case, chiropractic, decompression and movement-based strategies may be considered. The goal is not to promise reversal of normal aging; it is to improve symptoms and function when conservative care is appropriate. Imaging can help, but clinical correlation remains essential.',
    faqs: [
      { q: 'Can degenerative changes be reversed?', a: 'Care does not promise to reverse normal aging. The goal is to improve symptoms and function when conservative care is appropriate.' },
      { q: 'What conservative options might be considered?', a: 'Depending on the case, chiropractic, decompression and movement-based strategies may be considered after evaluation.' },
    ],
  },
  {
    slug: 'back-pain', condition: 'Back Pain', modality: 'Chiropractic & Decompression',
    title: 'Back Pain Chiropractor Austin | Disc, Spine & Decompression',
    description: 'Austin evaluation and conservative care for back pain. Disc, Spine & Decompression in Westlake Austin.',
    h1: 'Back Pain Care in Austin',
    intro: 'Low back pain is one of the most common reasons people seek chiropractic care, but it is not one diagnosis. Muscle, joint, disc, nerve and other factors can contribute, and the first step is identifying the likely pain generator and screening for red flags.',
    body: 'Chiropractic care may be appropriate for selected mechanical low-back presentations. Patients with disc-related or radiating presentations may also be evaluated for DRX9000 decompression. We track symptoms and function and modify recommendations according to response.',
    faqs: [
      { q: 'Is all back pain treated the same way?', a: 'No. Back pain has many contributors. We identify the likely pain generator, screen for red flags and individualize the plan.' },
      { q: 'When might decompression fit?', a: 'Patients with disc-related or radiating presentations may be evaluated for DRX9000 decompression as one conservative option.' },
    ],
  },
  {
    slug: 'neck-pain', condition: 'Neck Pain', modality: 'Cervical Spine Care',
    title: 'Neck Pain Chiropractor Austin | Cervical Spine Care',
    description: 'Austin evaluation and conservative care for neck pain. Disc, Spine & Decompression in Westlake Austin.',
    h1: 'Neck Pain Care in Austin',
    intro: 'Neck pain may be related to joints, muscles, discs, posture, injury or nerve irritation. Symptoms can remain local or travel into the shoulder, arm or hand.',
    body: 'We assess symptom behavior, range of motion, neurologic findings, injury history and other relevant factors. Appropriate cases may involve chiropractic care or other conservative strategies, and cervical decompression may be considered in selected disc-related presentations. Some headache patterns coexist with cervical musculoskeletal dysfunction, but headaches also have many non-musculoskeletal causes.',
    faqs: [
      { q: 'Can neck problems cause headaches?', a: 'Some headache patterns coexist with cervical musculoskeletal dysfunction, but headaches also have many non-musculoskeletal causes, so evaluation matters.' },
      { q: 'Is cervical decompression an option?', a: 'It may be considered in selected disc-related presentations after appropriate evaluation.' },
    ],
  },
  {
    slug: 'pinched-nerve', condition: 'Pinched Nerve', modality: 'Nerve Compression Evaluation',
    title: 'Pinched Nerve Treatment Austin | Nerve Compression Care',
    description: 'Austin evaluation and conservative care for a pinched (compressed) nerve. Disc, Spine & Decompression in Westlake Austin.',
    h1: 'Pinched Nerve Care in Austin',
    intro: 'A pinched or compressed nerve can cause radiating pain, numbness, tingling or weakness, depending on where along the spine the nerve is being irritated. Similar symptoms can arise from several sources, so evaluation matters before choosing a treatment.',
    body: 'We assess the symptom distribution, neurologic findings, injury history and any available imaging to understand where the nerve is being irritated. Depending on the findings, a conservative plan may include chiropractic care, DRX9000 decompression for appropriately selected disc-related presentations, or other measures. Progressive weakness or other red-flag findings warrant timely medical evaluation and possible referral.',
    faqs: [
      { q: 'What causes a pinched nerve?', a: 'Nerve irritation can relate to disc material, joint or bony changes, or soft-tissue factors. Evaluation helps identify the likely source before treatment.' },
      { q: 'Can it be treated without surgery?', a: 'Many presentations are managed conservatively. We evaluate candidacy for chiropractic care or decompression and refer when clinically indicated.' },
    ],
  },
  {
    slug: 'whiplash', condition: 'Whiplash', modality: 'Auto Accident Neck Injury Care',
    title: 'Whiplash Treatment Austin | Auto Accident Neck Injury',
    description: 'Austin evaluation and conservative care for whiplash after a motor-vehicle collision. Disc, Spine & Decompression in Westlake Austin.',
    h1: 'Whiplash Care in Austin',
    intro: 'Whiplash describes neck symptoms that can follow the rapid forces of a motor-vehicle collision. Symptoms such as neck pain, stiffness, headaches or reduced motion can appear right away or over the following days.',
    body: 'Early evaluation helps document the history and determine whether conservative chiropractic management is appropriate. We assess range of motion, neurologic findings and injury history, document findings, and recommend imaging or referral when clinically indicated. Treatment recommendations depend on the examination rather than a standard accident package.',
    faqs: [
      { q: 'How soon should I be evaluated after a collision?', a: 'Early evaluation helps document the injury and guide care. Seek prompt medical attention for severe or red-flag symptoms.' },
      { q: 'Do symptoms always appear immediately?', a: 'Not always. Some whiplash symptoms develop over the days following a collision, which is why evaluation is useful.' },
    ],
  },
];

// Resolve a condition entry into a single language-specific object.
export const resolveCondition = (base, lang) => {
  if (lang === 'es' && base.es) {
    const { es, ...rest } = base;
    return { ...rest, ...es };
  }
  return base;
};
